import { useMemo } from "react";
import { Link, useSearch, useLocation } from "wouter";
import { blogPosts, TAGS } from "@/data/blogPosts";
import BlogSearchAutocomplete from "@/components/BlogSearchAutocomplete";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Badge } from "@/components/ui/badge";
import { blogIndexSchema } from "@/data/schemaPatchGraphs";
import { CalendarDays, User, ArrowRight, Search, BookOpen, ChevronRight, Lock, X } from "lucide-react";
import { GooglePreferredSourceButton } from "@/components/GooglePreferredSourceButton";
import { ResponsiveImage } from "@/components/ResponsiveImage";

import { trackSectionCtaClick, trackCalendlyClick } from "@/lib/analytics";
import { HOMEPAGE_INSIGHT_FILTER_TAGS } from "@/lib/insightsSelector";
import { canonicalPath } from "@/lib/canonicalPath";
import { BLOG_TOPIC_ARCHIVES, blogTopicArchivePath, getBlogTopicArchiveBySlug, getBlogTopicArchiveByTag } from "@/lib/blogTopicArchives";
import { buildBlogIndexWinner09Schema } from "@/data/blogIndexWinner09";

import SchemaScript from "@/components/SchemaScript";
// ── AEO/LLMO Buyer's Guide Series data ───────────────────────────────────────
const AEO_SERIES = {
  label: "Series",
  title: "The AEO & LLMO Buyer's Guide",
  subtitle:
    "Four decision-stage guides for B2B marketers evaluating AI search investment. Each post answers a distinct question buyers ask before signing an AEO contract.",
  accentColor: "#F59E0B", // amber-500
  posts: [
    {
      num: 1,
      slug: "what-does-an-aeo-agency-uk-do-b2b-buyers-guide-2026",
      title: "What Does an AEO Agency in the UK Actually Do?",
      excerpt:
        "66% of UK senior decision-makers now use AI in procurement. Here is exactly what a UK AEO agency does, what to look for, and what questions to ask before you appoint one.",
      date: "2026-07-12",
      heroImage: "/media/aeo-agency-uk-buyer-guide_32ffb356.jpg",
      published: true,
    },
    {
      num: 2,
      slug: "aeo-agency-what-to-hire-for-2026",
      title: "What Should You Hire an AEO Agency For?",
      excerpt:
        "Use an evidence-led scorecard to assess what an AEO agency should own, what it should validate and how a B2B team should decide the next scope.",
      date: "2026-09-29",
      heroImage: "/media/aeo-agency-what-to-hire-for-2026-hero_bdb2d5dd.jpg",
      published: true,
    },
    {
      num: 3,
      slug: "aeo-agency-pricing-uk-cost-roi-guide-2026",
      title: "How Much Does AEO Cost? UK Pricing, Tiers and ROI Guide",
      excerpt:
        "UK AEO agency retainers range from £1,800 to £45,000 per month. The four-bucket cost model gives any CMO a 10-minute framework to evaluate any agency quote.",
      date: "2026-07-15",
      heroImage: "/media/aeo-agency-pricing-uk-cost-roi-guide-2026-hero_1e6615b8.png",
      published: true,
    },
    {
      num: 4,
      slug: "how-to-measure-aeo-results-b2b-analytics-guide-2026",
      title: "How Do You Measure AEO Results?",
      excerpt:
        "Only 14% of B2B brands track AI citations. The four-category framework, Share of Model, GA4 AI traffic setup, and the realistic timeline from first citation to pipeline contribution.",
      date: "2026-07-16",
      heroImage: "/media/aeo-measurement-analytics-guide-b2b-2026-hero_86176e4b.png",
      published: true,
    },
    {
      num: 5,
      slug: "aeo-agency-vs-in-house-vs-hybrid-b2b-decision-guide-2026",
      title: "AEO Agency vs In-House vs Hybrid: Which Model Fits Your B2B Brand?",
      excerpt:
        "A decision framework for CMOs: when to hire an agency, when to build in-house, and when the hybrid model is the only option that actually works.",
      date: "2026-07-17",
      heroImage: "/media/aeo-agency-vs-inhouse-hybrid-b2b-decision-guide-2026-hero_ac80d551.png",
      published: true,
    },
  ],
};

const POSTS_PER_PAGE = 12;

export default function Blog() {
  const searchString = useSearch();
  const [location, setLocation] = useLocation();
  const topicArchive = useMemo(() => {
    const match = location.match(/^\/blog\/topics\/([^/]+)\/?$/);
    return getBlogTopicArchiveBySlug(match?.[1]);
  }, [location]);
  const isTopicsIndex = /^\/blog\/topics\/?$/.test(location);

  // Derive this during render so server-side and static renders contain the
  // correct collection, rather than waiting for a browser-only effect.
  const requestedTag = topicArchive?.tag ?? new URLSearchParams(searchString).get("tag");
  const activeTag = requestedTag && TAGS.includes(requestedTag as any) ? requestedTag : null;

  const filteredPosts = useMemo(() => {
    if (!activeTag) return blogPosts;
    return blogPosts.filter((p) => p.tags.includes(activeTag));
  }, [activeTag]);
  const pageParam = Number.parseInt(new URLSearchParams(searchString).get("page") ?? "1", 10);
  const isTopicArchive = Boolean(topicArchive);
  const currentPage = (!activeTag || isTopicArchive) && Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  const totalPages = (!activeTag || isTopicArchive)
    ? Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE))
    : 1;
  const resolvedPage = Math.min(currentPage, totalPages);
  const displayedPosts = useMemo(() => {
    if (activeTag && !isTopicArchive) return filteredPosts;
    const start = (resolvedPage - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(start, start + POSTS_PER_PAGE);
  }, [activeTag, filteredPosts, isTopicArchive, resolvedPage]);
  const pageHref = (page: number) => {
    if (topicArchive) {
      const archivePath = blogTopicArchivePath(topicArchive);
      return canonicalPath(page <= 1 ? archivePath : `${archivePath}?page=${page}`);
    }
    return canonicalPath(page <= 1 ? "/blog/" : `/blog/?page=${page}`);
  };

  // Pre-compute post count per tag for the filter pills
  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    TAGS.forEach((t) => {
      counts[t] = blogPosts.filter((p) => p.tags.includes(t)).length;
    });
    return counts;
  }, []);

  const handleTagClick = (tag: string | null) => {
    const archive = getBlogTopicArchiveByTag(tag);
    const newUrl = archive ? blogTopicArchivePath(archive) : "/blog/";
    setLocation(newUrl);
  };

  // ── SEO meta: vary by path and query params ──────────────────────────────
  const isOldPath = location.startsWith("/ai-marketing-aeo-geo-blog");
  const params = new URLSearchParams(searchString);
  const categoryParam = params.get("category"); // old ?category= style (legacy)
  const tagParam = params.get("tag"); // legacy query-filter view
  const establishedQueryTopic = getBlogTopicArchiveByTag(tagParam);

  // Established query tags receive a server-side one-hop redirect to their
  // public archive. Keep only unrecognised filters out of the index.
  const isPaginatedArchive = !topicArchive && !tagParam && !categoryParam && resolvedPage > 1;
  const isPaginatedTopicArchive = isTopicArchive && resolvedPage > 1;
  const shouldNoindex = !topicArchive && (isOldPath || !!categoryParam || (!!tagParam && !establishedQueryTopic));
  const canonicalUrl = isTopicsIndex
    ? "https://integrated.social/blog/topics/"
    : topicArchive
      ? `https://integrated.social${pageHref(resolvedPage)}`
      : isPaginatedArchive
        ? `https://integrated.social/blog/?page=${resolvedPage}`
        : "https://integrated.social/blog/";
  const tagLabel = topicArchive?.name ?? tagParam ?? categoryParam ?? null;
  const metaTitle = isTopicsIndex
    ? "AI Marketing Blog Topics | Integrated.Social"
    : topicArchive
      ? isPaginatedTopicArchive
        ? `${topicArchive.name} Articles - Page ${resolvedPage} | Integrated.Social`
        : topicArchive.title
      : isPaginatedArchive
        ? `AI Marketing Blog - Page ${resolvedPage} | Integrated.Social`
        : tagLabel
          ? `${tagLabel} | AI Marketing Blog | Integrated.Social`
          : "AI Marketing Blog | AEO, GEO, AI Websites & B2B Growth Insights";
  const metaDescription = isTopicsIndex
    ? "Browse Integrated.Social's established AI marketing topic hubs: AEO, agentic AI, AI strategy, governance, paid media, news and practical digital marketing."
    : topicArchive
      ? isPaginatedTopicArchive
        ? `Page ${resolvedPage} of ${topicArchive.name} articles from Integrated.Social.`
        : topicArchive.description
      : isPaginatedArchive
        ? `Page ${resolvedPage} of the Integrated.Social AI marketing blog: evidence-led analysis of AEO, GEO, agentic AI and demand generation.`
        : tagLabel
          ? `Expert PoV on ${tagLabel}: AEO, Agentic AI, and B2B growth insights from Integrated.Social. Updated weekly for UK and US marketers.`
          : "Expert PoV on AEO, Agentic AI, Google AI Overviews, ChatGPT search ads, and B2B persona-driven growth. Updated weekly for UK and US marketers.";
  const topicArchiveSchema = isTopicsIndex ? {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": "https://integrated.social/blog/topics/#collection",
    url: "https://integrated.social/blog/topics/",
    name: "AI Marketing Blog Topics",
    description: "Established Integrated.Social editorial topic hubs for AI marketing and B2B growth.",
    inLanguage: "en-US",
    isPartOf: { "@id": "https://integrated.social/#website" },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: BLOG_TOPIC_ARCHIVES.map((archive, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: archive.name,
        url: `https://integrated.social${blogTopicArchivePath(archive)}`,
      })),
    },
  } : topicArchive ? {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${canonicalUrl}#collection`,
    url: canonicalUrl,
    name: topicArchive.title,
    description: topicArchive.description,
    inLanguage: "en-US",
    about: { "@type": "Thing", name: topicArchive.name },
    keywords: [topicArchive.name, "AI marketing", "B2B marketing"],
    isPartOf: { "@id": "https://integrated.social/#website" },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: displayedPosts.map((post, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: post.title,
        url: `https://integrated.social/blog/${post.slug}/`,
      })),
    },
  } : null;
  // Schema-only root correction: derive from the unchanged visible cards, not
  // stale hand-maintained recent URLs or article records with different labels.
  const rootIndexSchema = /^\/blog\/?$/.test(location) && !searchString
    ? buildBlogIndexWinner09Schema([...AEO_SERIES.posts.filter((post) => post.published), ...displayedPosts])
    : null;
  return (
    <>
      <SEOHead
        title={metaTitle}
        description={metaDescription}
        canonical={canonicalUrl}
        noindex={shouldNoindex}
      />
      {!shouldNoindex && <SchemaScript schema={JSON.stringify(rootIndexSchema ?? topicArchiveSchema ?? blogIndexSchema)} />}
      <Header />
      <main className="min-h-screen bg-background pt-24">
        {(topicArchive || isTopicsIndex) && (
          <Breadcrumbs
            items={[{ label: "Blog", href: "/blog/" }, { label: topicArchive?.name ?? "Topics" }]}
            className="container max-w-6xl mx-auto px-4 pt-4"
          />
        )}
        {/* Hero Section */}
        <section className="py-14 md:py-20 border-b border-border">
          <div className="container max-w-6xl mx-auto px-4">
            <h1 className="font-serif headline-xl text-foreground mb-5">
              {isTopicsIndex ? "AI Marketing Blog Topics" : topicArchive ? `${topicArchive.name} Articles` : "AI Growth Marketing & AEO Insights"}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed">
              {isTopicsIndex ? "Choose an established editorial topic hub to explore practical analysis, source-qualified news and decision guidance for AI marketing teams." : topicArchive ? topicArchive.answer : <>
                Weekly PoV on <strong className="text-foreground font-semibold">Answer Engine Optimisation</strong>,{" "}
                <strong className="text-foreground font-semibold">Agentic AI lead generation</strong>,{" "}
                <strong className="text-foreground font-semibold">Google AI Overviews</strong>, ChatGPT search ads,
                AI governance, and persona-driven B2B GTM. Written for enterprise marketers who need to win citations
                in AI-powered search, not just rankings.
              </>}
            </p>
            {topicArchive && (
              <div className="mt-6 max-w-3xl rounded-xl border border-amber-500/30 bg-amber-500/5 p-5">
                <h2 className="font-serif text-xl text-foreground">What this topic covers</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Browse the articles below for cited analysis and practical decision context. Need implementation support instead?
                </p>
                <Link href={topicArchive.service.href} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-amber-700 underline underline-offset-4 hover:text-amber-600">
                  Explore {topicArchive.service.label} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            )}
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <span className="text-sm text-muted-foreground">See more of our stories in Google Top Stories, AI Mode &amp; AI Overviews:</span>
              <GooglePreferredSourceButton variant="dark" />
            </div>
          </div>
        </section>

        {/* ── Search + Filter Bar - first interactive element ─────────────── */}
        <section className="py-5 border-b border-border bg-muted/30">
          <div className="container max-w-6xl mx-auto px-4">
            {/* Autocomplete search */}
            <BlogSearchAutocomplete
              onSearch={(q) => setLocation(`/blog/search?q=${encodeURIComponent(q)}`)}
              onTagSelect={(tag) => handleTagClick(tag)}
              className="mb-4"
            />

            {/* Homepage-aligned topic filter pills */}
            <p id="blog-topic-filter-label" className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-3">
              Browse by topic
            </p>
            <div
              className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 [scrollbar-width:thin]"
              role="group"
              aria-labelledby="blog-topic-filter-label"
            >
              <button
                type="button"
                onClick={() => handleTagClick(null)}
                aria-pressed={!activeTag}
                className={`shrink-0 min-h-11 rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 ${
                  !activeTag
                    ? "border-amber-500 bg-amber-500 text-slate-950"
                    : "border-border bg-background text-muted-foreground hover:border-amber-500/60 hover:text-foreground"
                }`}
              >
                All <span className="ml-1 text-xs opacity-70">({blogPosts.length})</span>
              </button>
              {HOMEPAGE_INSIGHT_FILTER_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleTagClick(tag)}
                  aria-pressed={activeTag === tag}
                  className={`shrink-0 min-h-11 rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 ${
                    activeTag === tag
                      ? "border-amber-500 bg-amber-500 text-slate-950"
                      : "border-border bg-background text-muted-foreground hover:border-amber-500/60 hover:text-foreground"
                  }`}
                >
                  {tag} <span className="ml-1 text-xs opacity-60">({tagCounts[tag] ?? 0})</span>
                </button>
              ))}
            </div>
            <p className="sr-only" aria-live="polite">
              Showing {activeTag ? `${activeTag} articles` : "all articles"}.
            </p>
          </div>
        </section>

        {!topicArchive && (
          <section className="border-b border-border bg-muted/20 py-10">
            <div className="container mx-auto max-w-6xl px-4">
              <h2 className="font-serif text-2xl text-foreground">Browse canonical topic hubs</h2>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
                Each topic hub collects the current articles for one established editorial theme and links to the most relevant service context.
              </p>
              <nav className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" aria-label="Blog topic hubs">
                {BLOG_TOPIC_ARCHIVES.map((archive) => (
                  <Link key={archive.slug} href={blogTopicArchivePath(archive)} className="group rounded-xl border border-border bg-card p-4 transition-colors hover:border-amber-500/60">
                    <span className="block font-semibold text-foreground group-hover:text-amber-700">{archive.name}</span>
                    <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">{archive.description}</span>
                  </Link>
                ))}
              </nav>
            </div>
          </section>
        )}

        {/* ── AEO/LLMO Buyer's Guide Series Spotlight ─────────────────────── */}
        {!activeTag && (
          <section className="py-12 md:py-16 border-b border-border bg-gradient-to-br from-amber-950/20 via-background to-background">
            <div className="container max-w-6xl mx-auto px-4">
              {/* Section header */}
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="w-4 h-4 text-amber-500" />
                    <span className="text-xs font-semibold uppercase tracking-widest text-amber-500">
                      {AEO_SERIES.label}
                    </span>
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl text-foreground leading-tight">
                    {AEO_SERIES.title}
                  </h2>
                  <p className="mt-2 text-sm md:text-base text-muted-foreground max-w-2xl leading-relaxed">
                    {AEO_SERIES.subtitle}
                  </p>
                </div>
                <button
                  onClick={() => handleTagClick("AEO & AI Search")}
                  className="shrink-0 flex items-center gap-1.5 text-sm font-medium text-amber-600 hover:text-amber-500 transition-colors"
                >
                  All AEO articles <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Series cards grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {AEO_SERIES.posts.map((post) => (
                  <div key={post.num} className="relative">
                    {post.published && post.slug ? (
                      <Link href={`/blog/${post.slug}/`}>
                        <article className="group h-full flex flex-col rounded-xl border border-border bg-card hover:border-amber-500/50 transition-all duration-200 overflow-hidden cursor-pointer">
                          {/* Thumbnail */}
                          <div className="aspect-[16/9] overflow-hidden bg-muted shrink-0">
                            <ResponsiveImage
                              src={post.heroImage!}
                              alt={`AEO buyer's guide part ${post.num}: ${post.title}`}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                              width={320}
                              height={180}
                              sizes="(min-width: 1280px) 216px, (min-width: 1024px) 30vw, (min-width: 640px) 45vw, calc(100vw - 32px)"
                            />
                          </div>
                          {/* Content */}
                          <div className="flex flex-col flex-1 p-4">
                            {/* Part badge */}
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 mb-2">
                              <span className="w-5 h-5 rounded-full bg-amber-500/15 flex items-center justify-center text-[10px] font-bold">
                                {post.num}
                              </span>
                              Part {post.num}
                            </span>
                            <h3 className="font-serif text-sm md:text-base text-foreground group-hover:text-amber-600 transition-colors leading-snug mb-2 line-clamp-3">
                              {post.title}
                            </h3>
                            <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3 flex-1">
                              {post.excerpt}
                            </p>
                            <div className="mt-3 flex items-center justify-between">
                              <span className="text-xs text-muted-foreground">
                                {new Date(post.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                              </span>
                              <span className="text-xs font-medium text-amber-600 flex items-center gap-0.5 group-hover:gap-1.5 transition-all">
                                Read <ArrowRight className="w-3 h-3" />
                              </span>
                            </div>
                          </div>
                        </article>
                      </Link>
                    ) : (
                      <article className="h-full flex flex-col rounded-xl border border-border/50 bg-card/50 overflow-hidden opacity-70">
                        {/* Placeholder thumbnail */}
                        <div className="aspect-[16/9] bg-muted/50 shrink-0 flex items-center justify-center">
                          <Lock className="w-6 h-6 text-muted-foreground/40" />
                        </div>
                        {/* Content */}
                        <div className="flex flex-col flex-1 p-4">
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground mb-2">
                            <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold">
                              {post.num}
                            </span>
                            Part {post.num}
                          </span>
                          <h3 className="font-serif text-sm md:text-base text-muted-foreground leading-snug mb-2 line-clamp-3">
                            {post.title}
                          </h3>
                          <p className="text-xs text-muted-foreground/70 leading-relaxed line-clamp-3 flex-1">
                            {post.excerpt}
                          </p>
                          <div className="mt-3">
                            <span className="text-xs text-muted-foreground/60 italic">{post.date}</span>
                          </div>
                        </div>
                      </article>
                    )}
                  </div>
                ))}
              </div>

              {/* Progress indicator */}
              <div className="mt-6 flex items-center gap-3">
                <div className="flex gap-1.5">
                  {AEO_SERIES.posts.map((p) => (
                    <div
                      key={p.num}
                      className={`h-1.5 rounded-full transition-all ${
                        p.published ? "w-8 bg-amber-500" : "w-4 bg-border"
                      }`}
                    />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">
                  {AEO_SERIES.posts.filter((p) => p.published).length} of {AEO_SERIES.posts.length} published
                </span>
              </div>
            </div>
          </section>
        )}

        {/* Sticky mini filter bar - visible while scrolling through posts */}
        <section className="py-2 border-b border-border bg-background/90 sticky top-16 z-30 backdrop-blur-sm">
          <div className="container max-w-6xl mx-auto px-4">
            <p id="blog-sticky-topic-filter-label" className="sr-only">Browse by topic</p>
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-[minmax(16rem,0.30fr)_minmax(0,1fr)] lg:items-center">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const val = (e.currentTarget.elements.namedItem("q") as HTMLInputElement)?.value?.trim();
                  if (val) setLocation(`/blog/search?q=${encodeURIComponent(val)}`);
                }}
                className="relative min-w-0"
                role="search"
                aria-label="Search blog articles"
              >
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  name="q"
                  type="search"
                  placeholder="Search articles…"
                  className="h-11 w-full rounded-lg border border-border bg-background/80 pl-10 pr-24 text-sm text-foreground placeholder:text-muted-foreground focus:bg-background focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors"
                />
                <button
                  type="submit"
                  className="absolute right-1.5 top-1/2 min-h-8 -translate-y-1/2 rounded-md bg-amber-500 px-3 text-xs font-semibold text-slate-950 transition-colors hover:bg-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
                >
                  Search
                </button>
              </form>
              <div
                className="flex flex-wrap items-center gap-2"
                role="group"
                aria-labelledby="blog-sticky-topic-filter-label"
              >
                <button
                  type="button"
                  onClick={() => handleTagClick(null)}
                  aria-pressed={!activeTag}
                  className={`min-h-11 rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 ${
                    !activeTag
                      ? "border-amber-500 bg-amber-500 text-slate-950"
                      : "border-border bg-background text-muted-foreground hover:border-amber-500/60 hover:text-foreground"
                  }`}
                >
                  All
                </button>
                {activeTag && (
                  <button
                    type="button"
                    onClick={() => handleTagClick(null)}
                    className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
                    aria-label={`Clear ${activeTag} topic filter`}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                    Clear topic
                  </button>
                )}
                {HOMEPAGE_INSIGHT_FILTER_TAGS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => handleTagClick(tag)}
                    aria-pressed={activeTag === tag}
                    className={`min-h-11 rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 ${
                      activeTag === tag
                        ? "border-amber-500 bg-amber-500 text-slate-950"
                        : "border-border bg-background text-muted-foreground hover:border-amber-500/60 hover:text-foreground"
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="py-16">
          <div className="container max-w-6xl mx-auto px-4">
            <h2 className="font-serif text-2xl md:text-3xl text-foreground mb-8">
              {activeTag ? `${activeTag} Articles` : "Latest AI Marketing Intelligence"}
            </h2>
            {activeTag && (
              <div className="mb-8 flex items-center gap-3">
                <p className="text-muted-foreground">
                  <span className="font-semibold text-foreground">{filteredPosts.length}</span>{" "}
                  {filteredPosts.length === 1 ? "post" : "posts"} in{" "}
                  <Badge variant="secondary" className="ml-1">{activeTag}</Badge>
                </p>
                <button
                  onClick={() => handleTagClick(null)}
                  className="text-xs text-amber-600 hover:text-amber-700 font-medium underline underline-offset-2"
                >
                  Clear
                </button>
              </div>
            )}

            <div className="grid gap-8 md:gap-12">
              {displayedPosts.map((post, index) => (
                <article
                  key={post.slug}
                  className="group md:grid md:grid-cols-5 md:gap-8 border-b border-border pb-8 md:pb-12"
                >
                  {/* Image */}
                  <div className="md:col-span-2 mb-4 md:mb-0">
                    <Link href={`/blog/${post.slug}/`}>
                      <div className="aspect-[16/10] overflow-hidden rounded-lg bg-muted">
                        <ResponsiveImage
                          src={post.heroImage}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          width={640}
                          height={400}
                          sizes="(min-width: 768px) 40vw, calc(100vw - 32px)"
                          priority={index < 2}
                        />
                      </div>
                    </Link>
                  </div>

                  {/* Content */}
                  <div className="md:col-span-3 flex flex-col justify-center">
                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {post.tags.slice(0, 3).map((tag) => {
                        const archive = getBlogTopicArchiveByTag(tag);
                        return archive ? (
                          <Link
                            key={tag}
                            href={blogTopicArchivePath(archive)}
                            className="text-xs font-medium px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 transition-colors"
                          >
                            {tag}
                          </Link>
                        ) : (
                          <span key={tag} className="text-xs font-medium px-2 py-0.5 rounded bg-muted text-muted-foreground">
                            {tag}
                          </span>
                        );
                      })}
                    </div>

                    {/* Title */}
                    <Link href={`/blog/${post.slug}/`}>
                      <h3 className="font-serif text-xl md:text-2xl text-foreground group-hover:text-amber-600 transition-colors duration-200 mb-3 leading-tight">
                        {post.title}
                      </h3>
                    </Link>

                    {/* Excerpt */}
                    <p className="text-muted-foreground text-sm md:text-base leading-relaxed mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {new Date(post.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-3.5 h-3.5" />
                        {post.author}
                      </span>
                      <Link href={`/blog/${post.slug}/`} className="ml-auto flex items-center gap-1 font-medium text-amber-600 hover:text-amber-700 transition-colors">
                        Read article <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {topicArchive && (
              <aside className="mt-12 border-t border-border pt-8" aria-labelledby="related-topic-hubs-heading">
                <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                  <div>
                    <h2 id="related-topic-hubs-heading" className="font-serif text-2xl text-foreground">Explore related AI marketing topics</h2>
                    <p className="mt-1 text-sm text-muted-foreground">Move between established editorial themes without relying on query-string tag filters.</p>
                  </div>
                  <Link href="/blog/" className="text-sm font-semibold text-amber-700 underline underline-offset-4 hover:text-amber-600">All AI marketing articles</Link>
                </div>
                <nav className="mt-5 flex flex-wrap gap-2" aria-label="Related blog topics">
                  {BLOG_TOPIC_ARCHIVES.filter((archive) => archive.slug !== topicArchive.slug).map((archive) => (
                    <Link
                      key={archive.slug}
                      href={blogTopicArchivePath(archive)}
                      className="rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-amber-500 hover:text-amber-700"
                    >
                      {archive.name}
                    </Link>
                  ))}
                </nav>
              </aside>
            )}

            {(!activeTag || isTopicArchive) && totalPages > 1 && (
              <nav className="mt-10 flex flex-col items-center gap-3 border-t border-border pt-8 text-center" aria-label={isTopicArchive ? "Topic pagination" : "Blog pagination"}>
                <p className="text-sm text-muted-foreground">
                  Page {resolvedPage} of {totalPages} · showing {displayedPosts.length} of {filteredPosts.length} articles.
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {resolvedPage > 1 && (
                    <Link href={pageHref(resolvedPage - 1)} className="rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-amber-500 hover:text-amber-700">
                      Previous
                    </Link>
                  )}
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                    <Link
                      key={page}
                      href={pageHref(page)}
                      aria-current={page === resolvedPage ? "page" : undefined}
                      className={`rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${page === resolvedPage ? "border-amber-500 bg-amber-500 text-slate-950" : "border-border text-foreground hover:border-amber-500 hover:text-amber-700"}`}
                    >
                      {page}
                    </Link>
                  ))}
                  {resolvedPage < totalPages && (
                    <Link href={pageHref(resolvedPage + 1)} className="rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-amber-500 hover:text-amber-700">
                      Next
                    </Link>
                  )}
                </div>
              </nav>
            )}

            {filteredPosts.length === 0 && (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg">No posts found for this topic.</p>
                <button onClick={() => handleTagClick(null)} className="mt-4 text-amber-600 hover:text-amber-700 font-medium">
                  View all posts
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
